/**
 * Timer Component
 * Displays and controls the active timer
 */

import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useTimeStore } from '../store/timeStore';
import { useClientStore } from '../store/clientStore';
import { formatDuration } from '../utils/formatters';
import './Timer.css';

const Timer = () => {
  const user = useAuthStore((state) => state.user);
  const runningTimer = useTimeStore((state) => state.runningTimer);
  const startTimer = useTimeStore((state) => state.startTimer);
  const stopTimer = useTimeStore((state) => state.stopTimer);
  const clients = useClientStore((state) => state.clients);

  const [selectedClient, setSelectedClient] = useState('');
  const [description, setDescription] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState('');

  // Update elapsed time every second when timer is running
  useEffect(() => {
    if (!runningTimer) {
      setElapsed(0);
      return;
    }

    const startTime = runningTimer.startTime.toDate();
    const interval = setInterval(() => {
      const now = new Date();
      const diff = Math.floor((now - startTime) / 1000);
      setElapsed(diff);
    }, 1000);

    return () => clearInterval(interval);
  }, [runningTimer]);

  const handleStart = async () => {
    if (!selectedClient) {
      setError('Please select a client');
      return;
    }

    try {
      setError('');
      await startTimer(user.uid, selectedClient, description);
      setDescription('');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStop = async () => {
    try {
      setError('');
      await stopTimer(runningTimer.id);
      setSelectedClient('');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="timer-container">
      <div className="timer-display">
        <div className="timer-time">{formatDuration(elapsed)}</div>
        {runningTimer && (
          <div className="timer-info">
            <span className="timer-client">{runningTimer.clientName}</span>
            {runningTimer.description && (
              <span className="timer-description">{runningTimer.description}</span>
            )}
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {!runningTimer ? (
        <div className="timer-controls">
          <select
            className="input"
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
          >
            <option value="">Select Client</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>

          <input
            type="text"
            className="input"
            placeholder="What are you working on?"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <button
            className="btn btn-success btn-lg"
            onClick={handleStart}
            disabled={!selectedClient}
          >
            ▶ Start Timer
          </button>
        </div>
      ) : (
        <div className="timer-controls">
          <button className="btn btn-danger btn-lg" onClick={handleStop}>
            ⏹ Stop Timer
          </button>
        </div>
      )}
    </div>
  );
};

export default Timer;
